import React, { useMemo, useState } from 'react';

import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  LinearProgress,
  Radio,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Tooltip,
  Typography,
  useMediaQuery,
} from '@mui/material';
import { createFilterOptions } from '@mui/material/Autocomplete';

import type { Campaign } from './data';
import { reportDateRange } from './reportFilters';
import {
  OFFER_FILTER_FIELDS,
  type OfferCatalogueFilters,
  filterReportOffers,
  newestReportOffers,
  reportOfferFilterValue,
  reportOfferWindows,
  searchReportOffers,
} from './reportSelection';

const matchingOptions = createFilterOptions<string>();
const columns = [
  { label: 'Select', width: '4%' },
  { label: 'Offer / opportunity', width: '21%' },
  { label: 'Hotel / vendor', width: '17%' },
  { label: 'Owner', width: '10%' },
  { label: 'Hotel country', width: '9%' },
  { label: 'Destination / state', width: '9%' },
  { label: 'Currency', width: '6%' },
  { label: 'Campaign dates', width: '12%' },
  { label: 'Sale / activity dates', width: '12%' },
];
type Props = {
  rows: Campaign[];
  cutoff: string;
  comparison: boolean;
  current: string;
  loading: boolean;
  error?: string;
  onRetry: () => void;
  onClose: () => void;
  onSelect: (id: string) => void;
};

export default function OfferPickerDialog({
  rows,
  cutoff,
  comparison,
  current,
  loading,
  error,
  onRetry,
  onClose,
  onSelect,
}: Props) {
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState('');
  const [page, setPage] = useState(0);
  const [filters, setFilters] = useState<OfferCatalogueFilters>({});
  const tall = useMediaQuery('(min-height: 950px)');
  const small = useMediaQuery('(max-width: 899px)');
  const pageSize = tall ? 8 : 5;
  const sorted = useMemo(() => newestReportOffers(rows), [rows]);
  const windows = useMemo(() => reportOfferWindows(rows, cutoff), [rows, cutoff]);
  const filtered = useMemo(
    () => filterReportOffers(searchReportOffers(sorted, search, comparison ? current : ''), filters),
    [sorted, search, comparison, current, filters],
  );
  const options = useMemo(
    () =>
      Object.fromEntries(
        OFFER_FILTER_FIELDS.map(({ key }) => {
          const candidates = filterReportOffers(rows, { ...filters, [key]: [] });
          const values = [...new Set(candidates.map((row) => reportOfferFilterValue(row, key)))];
          values.sort((a, b) => (key === 'year' ? b.localeCompare(a) : a.localeCompare(b)));
          return [key, values];
        }),
      ),
    [rows, filters],
  );
  const visiblePage = Math.min(page, Math.max(0, Math.ceil(filtered.length / pageSize) - 1));
  const validSelection = filtered.some((row) => row.offer_bk === selected) && !windows.get(selected)?.problem;
  const choose = (row: Campaign) => {
    if (!windows.get(row.offer_bk)?.problem) setSelected(row.offer_bk);
  };
  const radio = (row: Campaign) => (
    <Radio
      size="small"
      name="report-offer"
      disabled={!!windows.get(row.offer_bk)?.problem}
      checked={selected === row.offer_bk}
      onChange={() => choose(row)}
      inputProps={{
        'aria-label': `${row.offer_opportunity_name}, ${row.offer_bk}, ${row.start_date || 'Unknown start'} to ${row.end_date ?? 'End not supplied'}`,
      }}
    />
  );
  const pageRows = filtered.slice(visiblePage * pageSize, (visiblePage + 1) * pageSize);
  return (
    <Dialog
      open
      fullWidth
      maxWidth={false}
      onClose={onClose}
      aria-labelledby="offer-picker-title"
      sx={{
        '& .MuiDialog-paper': { width: 'calc(100% - 48px)', maxWidth: 1800, m: 2, maxHeight: 'calc(100dvh - 32px)' },
      }}
    >
      <DialogTitle id="offer-picker-title">{comparison ? 'Choose a comparison offer' : 'Choose an offer'}</DialogTitle>
      <DialogContent dividers sx={{ minWidth: 0, p: { xs: 1.5, md: 2 } }}>
        <Stack gap={1.5} sx={{ overflow: 'visible' }}>
          <TextField
            autoFocus
            size="small"
            label="Search offer, hotel, name or ID"
            value={search}
            onChange={(event) => {
              setSearch(event.target.value);
              setPage(0);
            }}
            fullWidth
          />
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, minmax(0, 1fr))', md: 'repeat(4, minmax(0, 1fr)) auto' },
              gap: 1,
            }}
          >
            {OFFER_FILTER_FIELDS.map(({ key, label }) => (
              <Autocomplete
                key={key}
                multiple
                size="small"
                limitTags={1}
                disabled={loading}
                options={options[key]}
                filterOptions={matchingOptions}
                value={filters[key] ?? []}
                onChange={(_, value) => {
                  setFilters((previous) => ({ ...previous, [key]: value }));
                  setPage(0);
                }}
                renderInput={(field) => <TextField {...field} label={label} placeholder="All" />}
              />
            ))}
            <Button
              size="small"
              disabled={!search && !Object.values(filters).some((values) => values.length)}
              onClick={() => {
                setFilters({});
                setSearch('');
                setPage(0);
              }}
            >
              Clear filters
            </Button>
          </Box>
          {loading && <LinearProgress aria-label="Loading offer catalogue" />}
          {error && (
            <Alert severity="error" action={<Button onClick={onRetry}>Retry</Button>}>
              {error}
            </Alert>
          )}
          {!loading && !error && (
            <>
              {small ? (
                <Stack gap={1}>
                  {pageRows.map((row, index) => (
                    <Box
                      key={row.offer_bk + index}
                      sx={{ border: 1, borderColor: selected === row.offer_bk ? 'primary.main' : 'divider', p: 1.5 }}
                    >
                      <Stack direction="row" alignItems="start" gap={1} sx={{ overflow: 'visible' }}>
                        {radio(row)}
                        <Box sx={{ minWidth: 0 }}>
                          <Typography variant="body2" fontWeight={600}>
                            {row.offer_opportunity_name}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {row.vendor_name} · {row.offer_country_name} · {row.cost_currency}
                          </Typography>
                          <Typography variant="body2">
                            Campaign: {reportDateRange(row.start_date, row.end_date)}
                          </Typography>
                          <Typography variant="caption">
                            First sale – last transaction:{' '}
                            {reportDateRange(row.first_sale_date, row.last_transaction_date)}
                          </Typography>
                          {windows.get(row.offer_bk)?.problem && (
                            <Typography variant="caption" color="error" display="block">
                              {windows.get(row.offer_bk).problem}
                            </Typography>
                          )}
                        </Box>
                      </Stack>
                    </Box>
                  ))}
                </Stack>
              ) : (
                <TableContainer role="region" aria-label="Offer catalogue" sx={{ overflow: 'visible' }}>
                  <Table
                    size="small"
                    sx={{
                      tableLayout: 'fixed',
                      width: '100%',
                      '& th, & td': { px: 1, py: 1, overflowWrap: 'anywhere', fontSize: 12.5, verticalAlign: 'top' },
                    }}
                  >
                    <TableHead>
                      <TableRow>
                        {columns.map((column) => (
                          <TableCell key={column.label} sx={{ width: column.width }}>
                            {column.label}
                          </TableCell>
                        ))}
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {pageRows.map((row, index) => (
                        <TableRow
                          key={row.offer_bk + index}
                          hover
                          selected={selected === row.offer_bk}
                          onClick={() => choose(row)}
                          sx={{ cursor: windows.get(row.offer_bk)?.problem ? 'default' : 'pointer' }}
                        >
                          <TableCell>{radio(row)}</TableCell>
                          <TableCell>
                            <Typography variant="body2" fontWeight={600}>
                              {row.offer_opportunity_name}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {row.offer_bk}
                            </Typography>
                            {windows.get(row.offer_bk)?.problem && (
                              <Typography variant="caption" color="error" display="block">
                                Dates unavailable
                              </Typography>
                            )}
                          </TableCell>
                          <TableCell>
                            {row.vendor_name || 'Unknown'}
                            {row.vendor_parent_name && row.vendor_parent_name !== 'Unknown' && (
                              <Typography variant="caption" color="text.secondary" display="block">
                                {row.vendor_parent_name}
                              </Typography>
                            )}
                          </TableCell>
                          <TableCell>{row.offer_owner_name || 'Unknown'}</TableCell>
                          <TableCell>{row.offer_country_name || 'Unknown'}</TableCell>
                          <TableCell>{row.offer_geo_area_level_1 || 'Unknown'}</TableCell>
                          <TableCell>{row.cost_currency || 'Unknown'}</TableCell>
                          <TableCell>
                            <Tooltip title="Campaign start – campaign end; Sydney dates">
                              <span>{reportDateRange(row.start_date, row.end_date)}</span>
                            </Tooltip>
                          </TableCell>
                          <TableCell>
                            <Tooltip title="First sale – last transaction; Sydney dates">
                              <span>{reportDateRange(row.first_sale_date, row.last_transaction_date)}</span>
                            </Tooltip>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
              {!filtered.length && <Alert severity="info">No offers match. Clear a filter or change the search.</Alert>}
              <TablePagination
                component="div"
                count={filtered.length}
                page={visiblePage}
                rowsPerPage={pageSize}
                rowsPerPageOptions={[pageSize]}
                onPageChange={(_, value) => setPage(value)}
              />
            </>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" disabled={loading || !!error || !validSelection} onClick={() => onSelect(selected)}>
          {comparison ? 'Add comparison' : 'Open report'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
